import { EvalRunStatus } from "~/pages/manifold-nextgen/types/eval-run";

const { VITE_CONVERSATIONS_API } = import.meta.env;

export const EVAL_CASES_API = `${VITE_CONVERSATIONS_API}/eval-cases`;
export const EVAL_RUNS_API = `${VITE_CONVERSATIONS_API}/eval-runs`;
export const EVAL_CASE_HISTORY_API = `${VITE_CONVERSATIONS_API}/eval-case-history`;
export const evalEvaluatorsPath = () => `${VITE_CONVERSATIONS_API}/eval-evaluators`;

/** A value an evaluator needs, rendered inline beside its toggle. */
export interface EvaluatorInput {
  /** The key the value is written under (an expectation, or a case column). */
  key: string;
  input_type: "text" | "number";
  label: string;
  hint: string;
  required: boolean;
  /** Whether the value is an expectation or a top-level case column. */
  target: "expectation" | "case_field";
  /** Inclusive lower bound for a numeric input. */
  min_value: number | null;
}

/**
 * `SelectableEvaluator` — one evaluator the case form can turn on, from
 * `GET /eval-evaluators`. The backend catalog is the source of truth for the
 * judge and assertion toggles and the companion value each needs, so the form
 * cannot drift from the vocabulary the runner actually reads.
 */
export interface SelectableEvaluator {
  /** The `expectations` or case key this evaluator is turned on by. */
  expectation_key: string;
  label: string;
  hint: string;
  kind: "judge" | "assertion";
  /** `flag` sets a boolean; `value` is turned on by supplying a value. */
  presence: "flag" | "value";
  /** A companion value authored inline with the evaluator, if any. */
  value_input: EvaluatorInput | null;
}

/**
 * Top of the judge score scale. The end-to-end judges (figure, text response)
 * all score 1-4, defined in the eval suites' `base_config.yaml`
 * judging instructions; a case's `score` is the mean of those. Unrelated to the
 * turn-diagnostics rubric, which is 1-5 — do not share a constant between them.
 */
export const EVAL_SCORE_MAX = 4;

/** The wire shape of `PaginatedResponse[T]` from the conversations API. */
export interface EvalPage<T> {
  items: T[];
  metadata: {
    limit: number;
    offset: number;
    total_count: number;
    has_more: boolean;
  };
}

/** `PartialEvalCase` — the list shape, without `inputs`/`expectations`. */
export interface PartialEvalCase {
  id: string;
  customer_id: string;
  name: string;
  description: string | null;
  tags: string[];
  dataset_ids: string[];
  pass_threshold: number | null;
  max_latency_seconds: number | null;
  /**
   * Monotonic revision of the case's scoring-relevant definition. Two results
   * are only comparable when their `case_version` matches.
   */
  version: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** `EvalCase` — the full case, as `GET /eval-cases/{id}` returns it. */
export interface EvalCase extends PartialEvalCase {
  inputs: Record<string, unknown>;
  expectations: Record<string, unknown>;
}

/**
 * `EvalCaseCreate`'s body. `extra="forbid"` on the API model means an unknown key
 * is a 422, so this stays a subset of the server schema.
 *
 * `inputs` and `expectations` are typed loosely for the reason the API types them
 * loosely: the suites own the vocabulary, and pinning it here would make the
 * client the thing that has to ship before a new evaluator can be used.
 */
export interface EvalCaseCreate {
  name: string;
  description?: string | null;
  tags: string[];
  dataset_ids: string[];
  inputs: Record<string, unknown>;
  expectations: Record<string, unknown>;
  pass_threshold?: number | null;
  max_latency_seconds?: number | null;
}

/** The subset of `EvalCaseUpdate` this UI edits. */
export interface EvalCaseUpdate {
  name?: string;
  description?: string | null;
  tags?: string[];
  pass_threshold?: number | null;
  max_latency_seconds?: number | null;
}

/** `PartialEvalRun` — the summary shape, without the config or report blobs. */
export interface PartialEvalRun {
  id: string;
  customer_id: string;
  tags: string[];
  case_created_by: string | null;
  triggered_by: string | null;
  status: EvalRunStatus;
  agent_id: string | null;
  started_at: string;
  completed_at: string | null;
  /** Written back by the runner on completion; never a launch input. */
  model_name: string | null;
}

/**
 * `run_parameters` as the launcher writes it and the runner reads it back. The API
 * validates it against a strict `RunParameters` model on the way in, so a stored
 * blob has exactly these keys; a run created before a key existed carries none.
 */
export interface EvalRunParameters {
  iterations?: number;
  max_concurrency?: number;
  rewrite_queries?: boolean;
  rewrite_model?: string;
  keep_conversations?: boolean;
}

/**
 * `report` as the eval runner patches it on completion: the counts it observed,
 * plus `html`, the rendered pydantic-evals report embedded so it can be read
 * without reaching into S3. `html` is best-effort; `error` appears only on a run
 * the runner marked failed, and is the reason it did.
 */
export interface EvalRunReport {
  num_cases?: number;
  iterations?: number;
  results_posted?: number;
  html?: string;
  error?: string;
}

/**
 * `EvalRunListItem` — the runs-list row: a run's summary plus its launch
 * configuration (`run_parameters`, `judge_config`), so the dashboard can group
 * runs by the configuration they were judged under. The report stays omitted;
 * it is access-gated and reachable only per run.
 */
export interface EvalRunListItem extends PartialEvalRun {
  judge_config: Record<string, string> | null;
  run_parameters: EvalRunParameters;
}

/** `EvalRun` — a single run in full, with its configuration and report. */
export interface EvalRun extends EvalRunListItem {
  report: EvalRunReport | null;
  report_s3_key: string | null;
}

/**
 * The `judge_config` keys naming a model, in rubric order.
 *
 * A run records the *effective* judge configuration the runner resolved, not
 * just the override that was asked for, so these are the models a past run's
 * scores actually came from. That matters when comparing two runs: a score that
 * moved because the judge model changed is not the agent getting better or
 * worse.
 */
export const JUDGE_MODEL_KEYS = [
  { key: "text_response_judge_model", label: "Text response judge" },
  { key: "figure_judge_model", label: "Figure judge" },
] as const;

/**
 * One evaluator's judgement. `value` is a 1-4 score for the judge evaluators
 * and a boolean for the assertion ones (table containment, tool-call counts).
 * `error: true` marks an evaluator that raised — it produced neither, and the
 * runner records it as a failure rather than a silent pass.
 */
export interface EvaluatorScore {
  value: number | boolean | null;
  reason: string | null;
  error?: boolean;
}

/** `PartialEvalCaseResult` — one case-iteration outcome, list shape. */
export interface PartialEvalCaseResult {
  id: string;
  run_id: string;
  case_id: string;
  case_version: number;
  iteration: number;
  score: number | null;
  passed: boolean | null;
  conversation_id: string | null;
  error: string | null;
  /**
   * Timing the runner recorded: `task_duration_seconds` (the agent turn) and
   * `total_duration_seconds` (turn plus judging). On the list shape because
   * finding the slow case in a run means comparing every case in it.
   */
  metrics: Record<string, number> | null;
  /**
   * Each judge's 1-4 score, keyed by evaluator name (`TextResponseEvaluator`,
   * `FigureEvaluator`) — the numeric part of `evaluator_scores`, so the
   * case-health grid can show text and figure scores apart. Null when the
   * result recorded no judge score.
   */
  judge_scores: Record<string, number> | null;
}

/** `EvalCaseResult` — one outcome with its full evaluator detail. */
export interface EvalCaseResult extends PartialEvalCaseResult {
  evaluator_scores: Record<string, EvaluatorScore> | null;
}

/** `EvalRunCreate`'s launch body. `agent_id` is required — a null value is 422. */
export interface EvalRunLaunch {
  agent_id: string;
  tags: string[];
  case_created_by?: string;
  run_parameters: {
    iterations: number;
    max_concurrency: number;
    rewrite_queries: boolean;
    keep_conversations: boolean;
  };
  judge_config?: Record<string, string>;
}

type QueryValue = string | number | string[] | null | undefined;

/**
 * A query string over params that may repeat.
 *
 * An array appends one param per element, which is how the API's
 * `list[str] | None` filters arrive. An absent value is omitted rather than sent
 * empty: a `status=` with nothing after it is a 422, not "no filter".
 */
function withQuery(base: string, params: Record<string, QueryValue>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    for (const item of Array.isArray(value) ? value : [value]) {
      if (item !== undefined && item !== null && item !== "") {
        qs.append(key, String(item));
      }
    }
  }
  const query = qs.toString();
  return query ? `${base}?${query}` : base;
}

/**
 * Repeatable `tags` params, matching the API's `list[str] | None` query filter.
 * A case must carry every tag listed (PostgreSQL `@>` containment), so this is
 * an AND, not an OR.
 */
function withTags(base: string, tags: string[], rest: Record<string, number>): string {
  return withQuery(base, { tags, ...rest });
}

export const evalCasesPath = ({
  tags = [],
  limit = 200,
  offset = 0,
}: { tags?: string[]; limit?: number; offset?: number } = {}) =>
  withTags(EVAL_CASES_API, tags, { limit, offset });

export const evalCasePath = (caseId: string) => `${EVAL_CASES_API}/${caseId}`;

export const evalRunsPath = ({
  tags = [],
  limit = 50,
  offset = 0,
}: { tags?: string[]; limit?: number; offset?: number } = {}) =>
  withTags(EVAL_RUNS_API, tags, { limit, offset });

export const evalRunPath = (runId: string) => `${EVAL_RUNS_API}/${runId}`;

export const evalRunLaunchPath = () => `${EVAL_RUNS_API}/launch`;

/**
 * A run's results. The API caps `limit` at 500 and a run is cases x iterations,
 * so the default asks for the cap: the matrix needs whole runs, not pages.
 */
export const evalRunResultsPath = (runId: string, { limit = 500, offset = 0 } = {}) =>
  withQuery(`${EVAL_RUNS_API}/${runId}/results`, { limit, offset });

export const evalRunResultPath = (runId: string, resultId: string) =>
  `${EVAL_RUNS_API}/${runId}/results/${resultId}`;

/**
 * `EvalCaseHistory` — every selected case's results from its *own* newest runs.
 *
 * One request rather than one per run: results are otherwise reachable only per
 * run, and a window of the newest runs overall gives a case only the subset of
 * them that selected it.
 */
export interface EvalCaseHistory {
  /** Runs each case was windowed to. A case exercised fewer times has fewer. */
  runs_per_case: number;
  /** Every iteration of every case in its own window, unpaginated. */
  results: PartialEvalCaseResult[];
  /** The runs `results` name, so a cell can label itself. Not a page of runs. */
  runs: PartialEvalRun[];
  /** Recent runs left out because the caller cannot read a dataset or agent. */
  hidden_run_count: number;
}

/**
 * Each case's history. `runsPerCase` is the depth per case, not a page size —
 * the response carries every case, so there is no offset to page with.
 */
export const evalCaseHistoryPath = ({
  tags = [],
  agentIds = [],
  status,
  runsPerCase,
}: {
  tags?: string[];
  agentIds?: string[];
  status?: string;
  runsPerCase: number;
}) =>
  withQuery(EVAL_CASE_HISTORY_API, {
    tags,
    agent_id: agentIds,
    status,
    runs_per_case: runsPerCase,
  });
